import React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Input - Komponent pola tekstowego w stylu Radix Themes
 * 
 * API zgodne z @radix-ui/themes
 * - Rozmiary: "1" | "2" | "3"
 * - Warianty: "classic" | "surface" | "soft" | "ghost"
 * 
 * @example
 * ```tsx
 * <Input 
 *   variant="surface" 
 *   size="2"
 *   placeholder="Wpisz tekst"
 * />
 * ```
 */

const inputVariants = cva(
  cn(
    "flex w-full",
    "font-[var(--font-weight-regular)]",
    "transition-all duration-150",
    "focus:outline-none focus:ring-3 focus:ring-[var(--pink-9)] focus:ring-offset-1", // WCAG AAA enhanced focus
    "disabled:cursor-not-allowed disabled:opacity-50",
    "placeholder:text-[var(--text-white-60)]", // WCAG AAA compliant (8:1 ratio)
    "file:border-0 file:bg-transparent file:text-sm file:font-medium"
  ),
  {
    variants: {
      variant: {
        // Classic - z wyraźnym borderem
        classic: cn(
          "bg-[var(--dark-2)]",
          "border border-[var(--dark-a7)]",
          "text-[var(--text-white-95)]",
          "hover:border-[var(--pink-a6)]",
          "shadow-[0_1px_2px_0_rgba(0,0,0,0.1)]"
        ),
        // Surface - domyślny, subtelny
        surface: cn(
          "bg-[var(--dark-2)]",
          "border border-[var(--dark-a5)]",
          "text-[var(--text-white-95)]",
          "hover:border-[var(--pink-a4)]",
          "shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
        ),
        // Soft - bardzo subtelne
        soft: cn(
          "bg-[var(--dark-a2)]",
          "border border-transparent",
          "text-[var(--text-white-95)]",
          "hover:bg-[var(--dark-a3)]"
        ),
        // Ghost - minimalne stylowanie
        ghost: cn(
          "bg-transparent",
          "border border-transparent",
          "text-[var(--text-white-95)]",
          "hover:bg-[var(--dark-a2)]"
        ),
      },
      size: {
        "1": cn(
          "h-[24px]",
          "px-[var(--space-3)] py-[var(--space-1)]",
          "text-[var(--font-size-1)]",
          "rounded-[var(--radius-sm)]"
        ),
        "2": cn(
          "h-[32px]",
          "px-[var(--space-4)] py-[var(--space-2)]",
          "text-[var(--font-size-2)]",
          "rounded-[var(--radius-base)]"
        ),
        "3": cn(
          "h-[40px]",
          "px-[var(--space-5)] py-[var(--space-3)]",
          "text-[var(--font-size-3)]",
          "rounded-[var(--radius-md)]"
        ),
      },
      // Error state
      error: {
        true: "border-[var(--danger-9)] focus:ring-[var(--danger-9)]",
        false: "",
      },
      // Success state
      success: {
        true: "border-[var(--success-9)] focus:ring-[var(--success-9)]",
        false: "",
      },
    },
    defaultVariants: {
      variant: "surface",
      size: "2",
      error: false,
      success: false,
    },
  }
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, size, error, success, type = "text", ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(inputVariants({ variant, size, error, success }), className)}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

/**
 * Textarea - Wieloliniowe pole tekstowe
 */
export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof inputVariants> {
  resize?: boolean
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant, error, success, resize = true, ...props }, ref) => {
    return (
      <textarea
        className={cn(
          inputVariants({ variant, error, success }),
          "min-h-[80px] py-[var(--space-3)]",
          resize ? "resize-y" : "resize-none",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Textarea.displayName = "Textarea"

export { Input, Textarea, inputVariants }

